import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, limit, documentId } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { CATEGORY_LABELS, type PublicProfileDoc } from '@makhe/shared';

export default function FavouritesPage() {
  const { userDoc } = useAuth();
  const ids = userDoc?.preferences?.favouriteSellers ?? [];

  const q = useMemo(() => {
    if (ids.length === 0) return null;
    return query(
      collection(db, 'publicProfiles'),
      where(documentId(), 'in', ids.slice(0, 10)),
      limit(10),
    );
  }, [ids]);

  const { data: sellers, loading, error } = useCollection<PublicProfileDoc>(q);
  const CAP = 10;

  return (
    <section className="mx-auto max-w-2xl px-5 py-6 pb-10 md:px-6">
      <div className="border-b border-makhe-line pb-4">
        <p className="eyebrow mb-2">Saved</p>
        <h1
          className="font-display text-3xl leading-none md:text-4xl"
          style={{ fontVariationSettings: '"opsz" 72, "wght" 450' }}
        >
          Creators
        </h1>
      </div>
      {ids.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="No saved creators yet"
            description="Tap the heart on a feed card to save someone for later."
            action={
              <Link to="/feed" className="btn-primary">
                Open feed
              </Link>
            }
          />
        </div>
      ) : loading ? (
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          <LoadingSkeleton className="h-20" />
          <LoadingSkeleton className="h-20" />
        </div>
      ) : error ? (
        <div className="mt-6">
          <EmptyState
            title="Couldn’t load your saved creators"
            description="Something went wrong. Check your connection and try again."
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {ids.length > CAP && (
            <p className="md:col-span-2 text-xs text-makhe-muted">
              Showing {CAP} of {ids.length} saved creators.
            </p>
          )}
          {sellers.map((s) => (
            <Link
              key={s.uid}
              to={`/seller/${s.uid}`}
              className="flex items-center gap-3 rounded-lg border border-makhe-line bg-makhe-paper p-4 shadow-soft transition-shadow hover:shadow-lift"
            >
              <div className="h-12 w-12 overflow-hidden rounded-full bg-makhe-line/50">
                {s.photoURL && (
                  <img src={s.photoURL} alt="" loading="lazy" className="h-full w-full object-cover" />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate font-display text-lg leading-tight">{s.displayName}</p>
                <p className="text-xs text-makhe-muted">
                  {s.sellerProfile?.categories.map((c) => CATEGORY_LABELS[c]).join(' · ')}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
